import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faCheck } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import ParticleNetwork from '../components/ParticleNetwork';
import '../styles/AboutPage.css';

const AboutPage = () => {
  const { t } = useTranslation();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="about-hero">
        <ParticleNetwork />
        <div className="container">
          <motion.div
            className="hero-centered-content"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            <div className="breadcrumbs">
              <Link to="/">{t('nav.home')}</Link>
              <span className="separator">/</span>
              <span className="current">{t('nav.about')}</span>
            </div>

            <motion.div className="hero-main" variants={itemVariants}>
              <h1 className="hero-title">
                {t('about.title')}
              </h1>
              <div className="hero-accent-line"></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="about-details">
        <div className="container">
          <div className="details-grid">
            <motion.div
              className="details-image"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <img src="./images/kusgrup.png" alt="KUS GRUP Tesis" />
            </motion.div>

            <motion.div
              className="details-text"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="section-subtitle">{t('about.subtitle')}</h2>
              <p>
                {t('about.desc_1')}
              </p>
              <p>
                {t('about.desc_2')}
              </p>

              <div className="features-highlight">
                <h3>{t('about.why_us')}</h3>
                <ul className="modern-features-list">
                  <li><FontAwesomeIcon icon={faCheckCircle} /> <span>{t('about.quality')}</span></li>
                  <li><FontAwesomeIcon icon={faCheckCircle} /> <span>{t('about.delivery')}</span></li>
                  <li><FontAwesomeIcon icon={faCheckCircle} /> <span>{t('about.price')}</span></li>
                  <li><FontAwesomeIcon icon={faCheckCircle} /> <span>{t('about.customer')}</span></li>
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission & Vision Cards */}
      <section className="mission-vision-cards">
        <div className="container">
          <div className="cards-grid">
            <motion.div
              className="mv-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="card-accent"></div>
              <h3>{t('about.mission')}</h3>
              <p>
                {t('about.mission_text')}
              </p>
            </motion.div>

            <motion.div
              className="mv-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="card-accent gold"></div>
              <h3>{t('about.vision')}</h3>
              <p>
                {t('about.vision_text')}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Products Overview */}
      <section className="products-overview">
        <div className="container">
          <div className="overview-header">
            <h2 className="section-title">{t('about.products_services')}</h2>
            <div className="title-underline"></div>
          </div>

          <div className="overview-grid">
            <motion.div
              className="overview-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3>{t('about.p1_title')}</h3>
              <p>{t('about.p1_desc')}</p>
              <ul className="minimal-list">
                <li><FontAwesomeIcon icon={faCheck} /> {t('about.p1_li1')}</li>
                <li><FontAwesomeIcon icon={faCheck} /> {t('about.p1_li2')}</li>
                <li><FontAwesomeIcon icon={faCheck} /> {t('about.p1_li3')}</li>
              </ul>
            </motion.div>

            <motion.div
              className="overview-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              <h3>{t('about.p2_title')}</h3>
              <p>{t('about.p2_desc')}</p>
            </motion.div>

            <motion.div
              className="overview-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4 }}
            >
              <h3>{t('about.p3_title')}</h3>
              <p>{t('about.p3_desc')}</p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
